import React,{useCallback,useEffect,useState} from "react";
import {useAtom} from "jotai";
import {useNavigate} from "react-router-dom";
import styled from "styled-components";
import Table from "../../components/table";
import {dataTotalInfo} from "../../components/common/entity";
import {columnNotice} from "./entity/NoticeEntity";
import WriteNoticeModal from "../../components/common/WriteNoticeModal";
import {selNoticeList} from "../../services/notice/NoticeAxios";
import {createNoticeAdmin,selNoticeListAdmin} from "../../services/notice/NoticeAdminAxios";
import {tokenResultAtom} from "../login/entity";
import PlatformSectionHeader from "../platform_manage/PlatformSectionHeader";
import PlatformTableBody from "../platform_manage/PlatformTableBody";

const initialCondition={keyword:"",pageSize:10,currentPage:1,publishYn:"ALL"};

export default function Notice(){
  const [token]=useAtom(tokenResultAtom);
  const navigate=useNavigate();
  const [total,setTotal]=useState(dataTotalInfo);
  const [draftCondition,setDraftCondition]=useState({...initialCondition});
  const [appliedCondition,setAppliedCondition]=useState({...initialCondition});
  const [searchVersion,setSearchVersion]=useState(0);
  const [open,setOpen]=useState(false);
  const isAdmin=token.role!=="NORMAL";
  useEffect(()=>{if(token.role)setSearchVersion(v=>v+1);},[token.role]);

  const refresh=()=>setSearchVersion(v=>v+1);
  const search=()=>{
    setAppliedCondition({...draftCondition,currentPage:1});
    refresh();
  };
  const reset=()=>{
    const resetCondition={...initialCondition};
    setDraftCondition(resetCondition);
    setAppliedCondition(resetCondition);
    refresh();
  };
  const createNotice=data=>createNoticeAdmin(data).then(refresh);
  const dataSource=useCallback(({skip,limit})=>{
    if(searchVersion<0)return Promise.resolve({data:[],count:0});
    const params={...appliedCondition,currentPage:skip/limit+1,pageSize:limit};
    const request=isAdmin?selNoticeListAdmin(params):selNoticeList({...params,publishYn:"Y"});
    return request.then(response=>{
      const rows=response?.content||response?.rows||[];
      const count=response?.totalElements??response?.totalCount??rows.length;
      setTotal({totalCount:count,currentCount:rows.length,currentPage:response?.pageNumber,totalPages:response?.totalPages});
      return {data:rows,count:Number(count)};
    });
  },[appliedCondition,isAdmin,searchVersion]);

  const noticeColumns=(isAdmin?columnNotice:columnNotice.filter(column=>column.name!=="publishYn")).map(column=>
    column.name==="publishYn"
      ? {...column,render:({value})=><PublishStatus $published={value==="Y"}>{value==="Y"?"공개":"비공개"}</PublishStatus>}
      : column
  );

  const action=isAdmin?<AddAction><WriteNoticeModal formType="notice" onClick={createNotice} title="공지사항 작성" buttonText="+ 공지사항 추가"/></AddAction>:null;
  return <NoticePage>
    <Card $open={open}>
      <PlatformSectionHeader title="조회 조건" description="공지사항 제목과 게시 여부를 기준으로 목록을 조회합니다." collapsible open={open} onToggle={()=>setOpen(v=>!v)}/>
      {open&&<ConditionBody>
        <FilterGrid>
          <Field><Label>공지사항 검색</Label><SearchWrap><SearchIcon>⌕</SearchIcon><input placeholder="공지사항 제목 검색" value={draftCondition.keyword}
            onChange={e=>setDraftCondition(prev=>({...prev,keyword:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&search()}/></SearchWrap></Field>
          {isAdmin&&<Field $compact><Label>게시 여부</Label><select value={draftCondition.publishYn} onChange={e=>setDraftCondition(prev=>({...prev,publishYn:e.target.value}))}>
            <option value="ALL">전체</option><option value="Y">게시</option><option value="N">미게시</option>
          </select></Field>}
        </FilterGrid>
        <ConditionFooter><span>조건을 설정한 뒤 적용 버튼을 눌러 공지사항 목록을 조회합니다.</span><div><Reset onClick={reset}>초기화</Reset><Apply onClick={search}>적용</Apply></div></ConditionFooter>
      </ConditionBody>}
    </Card>
    <Card>
      <PlatformSectionHeader title="공지사항 목록" description="등록된 공지사항과 게시 상태를 확인합니다." count={total.totalCount||0} unit="건" action={action}/>
      <PlatformTableBody>
        <Table columns={noticeColumns} defaultLimit={appliedCondition.pageSize}
          data={dataSource} onRowClick={({data})=>data&&navigate("/board/noticeDetail",{state:{data}})}/>
      </PlatformTableBody>
    </Card>
  </NoticePage>;
}
const NoticePage=styled.div`display:flex;width:100%;flex-direction:column;gap:16px;`;
const Card=styled.section`position:relative;z-index:${({$open})=>$open?20:1};overflow:${({$open})=>$open?"visible":"hidden"};border:1px solid var(--color-border);border-radius:10px;background:var(--color-surface);box-shadow:0 5px 18px rgba(31,41,55,.045);`;
const ConditionBody=styled.div`padding:18px 20px 16px;border-top:1px solid var(--color-divider);`;
const FilterGrid=styled.div`display:grid;grid-template-columns:minmax(280px,1fr) minmax(160px,240px);gap:16px;@media(max-width:720px){grid-template-columns:1fr;}`;
const Field=styled.div`display:flex;min-width:0;flex-direction:column;gap:7px;select{height:42px;padding:0 12px;border:1px solid #dfe3e7;border-radius:8px;background:var(--color-surface);color:#444;font-size: var(--font-size-13);outline:0;}select:focus{border-color:var(--color-primary);box-shadow:0 0 0 3px rgba(59,130,246,.1);}`;
const Label=styled.label`color:#444;font-size: var(--font-size-13);font-weight:700;`;
const SearchWrap=styled.div`position:relative;input{width:100%;height:42px;padding:0 13px 0 36px;border:1px solid #dfe3e7;border-radius:8px;font-size: var(--font-size-13);outline:0;}input:focus{border-color:var(--color-primary);box-shadow:0 0 0 3px rgba(59,130,246,.1);}`;
const SearchIcon=styled.span`position:absolute;top:50%;left:13px;color:#8b96a2;font-size: var(--font-size-19);transform:translateY(-52%);`;
const ConditionFooter=styled.div`display:flex;align-items:center;justify-content:space-between;margin-top:18px;padding-top:14px;border-top:1px solid var(--color-divider);color:#929ba5;font-size: var(--font-size-11);div{display:flex;gap:8px;}`;
const Reset=styled.button`height:38px;padding:0 16px;border:1px solid #dfe3e7;border-radius:7px;background:var(--color-surface);color:#68717c;font-size: var(--font-size-13);font-weight:650;`;
const Apply=styled(Reset)`border-color:var(--color-primary-strong);background:var(--color-primary-strong);color:var(--color-surface);`;
const AddAction=styled.div`button{min-width:110px;height:40px;padding:0 16px;border:1px solid var(--color-primary-border)!important;border-radius:7px!important;background:var(--color-primary-soft)!important;color:var(--color-primary-strong)!important;font-size: var(--font-size-13)!important;font-weight:700!important;}button:hover{background:var(--color-primary-muted)!important;}`;
const PublishStatus=styled.span`
  display:inline-flex;align-items:center;justify-content:center;min-width:52px;height:24px;padding:0 10px;
  border:1px solid ${({$published})=>$published?"#b7e4cd":"#f3b7b7"};
  border-radius:999px;background:${({$published})=>$published?"#edf9f3":"#fff1f1"};
  color:${({$published})=>$published?"#168253":"#d04444"};font-size: var(--font-size-12);font-weight:700;
`;
